import { useCallback, useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;
export interface SortState {
  key: string | null;
  direction: SortDirection;
}

export interface UseSortResult<T> {
  sortedItems: T[];
  sort: SortState;
  toggle: (key: string) => void;
}

/**
 * Three-state column sort: unsorted → asc → desc → unsorted.
 * `getValue(item, key)` extracts the comparable for the given column key.
 * Null/undefined values sort to the end regardless of direction.
 */
export function useSort<T>(
  items: T[],
  getValue: (item: T, key: string) => unknown,
  initialSort?: SortState,
): UseSortResult<T> {
  const [sort, setSort] = useState<SortState>(
    initialSort ?? { key: null, direction: null },
  );

  const sortedItems = useMemo(() => {
    if (!sort.key || !sort.direction) return items;
    const key = sort.key;
    const dir = sort.direction;
    return [...items].sort((a, b) => {
      const av = normalize(getValue(a, key));
      const bv = normalize(getValue(b, key));
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [items, sort, getValue]);

  const toggle = useCallback((key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: null };
    });
  }, []);

  return { sortedItems, sort, toggle };
}

function normalize(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'boolean') return value ? 1 : 0;
  return String(value).toLowerCase();
}
